import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  IconArrowDownLeft,
  IconCalculator,
  IconCheck,
  IconRotate,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { returnItems } from "../../controllers/item_issue.controller";
import toast from "react-hot-toast";

export default function ReturnItemsView({
  register,
  eligibleItems,
  users,
  prefilledRow,
  onReturnSuccess,
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [verifiedBy, setVerifiedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Group register by user to find custodians with active stock
  const activeCustodians = useMemo(() => {
    const map = new Map();
    (register || []).forEach((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;
      if (!isClosed && remaining > 0.0001) {
        if (!map.has(r.user_id)) {
          map.set(r.user_id, {
            value: r.user_id,
            label: `${r.employee_name || r.user_id} (${r.department || "Staff"})`,
            items: [],
          });
        }
        map.get(r.user_id).items.push(r);
      }
    });
    return Array.from(map.values());
  }, [register]);

  // Handle prefilled row from Custody Register action
  useEffect(() => {
    if (prefilledRow) {
      const uOpt = activeCustodians.find((c) => c.value === prefilledRow.user_id) || {
        value: prefilledRow.user_id,
        label: `${prefilledRow.employee_name || prefilledRow.user_id} (${prefilledRow.department || "Staff"})`,
      };
      setSelectedUser(uOpt);

      const matchingItem = (eligibleItems || []).find(
        (i) => i.id === prefilledRow.inventory_item_id
      );
      if (matchingItem) {
        setSelectedItem({
          value: matchingItem.id,
          label: `${matchingItem.title} (Remaining: ${prefilledRow.remaining_base_quantity} ${matchingItem.unit})`,
          item: matchingItem,
          custodyRecord: prefilledRow,
        });
      }
    }
  }, [prefilledRow, activeCustodians, eligibleItems]);

  // Items held by the selected custodian
  const heldItemOptions = useMemo(() => {
    if (!selectedUser) return [];
    const held = (register || []).filter((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;
      return r.user_id === selectedUser.value && !isClosed && remaining > 0.0001;
    });

    return held.map((r) => {
      const matchingMaster = (eligibleItems || []).find((i) => i.id === r.inventory_item_id);
      return {
        value: r.inventory_item_id,
        label: `${r.item_title} — Custody Rem: ${r.remaining_base_quantity} ${r.unit} (Pending: ${r.pending_sales_base_quantity || 0})`,
        item: matchingMaster || { id: r.inventory_item_id, title: r.item_title, unit: r.unit, alternate_units: [] },
        custodyRecord: r,
      };
    });
  }, [selectedUser, register, eligibleItems]);

  // Unit options for selected item
  const unitOptions = useMemo(() => {
    if (!selectedItem) return [];
    const item = selectedItem.item;
    const list = [
      {
        value: `base_${item.unit}`,
        label: `${item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      },
    ];

    if (Array.isArray(item.alternate_units)) {
      item.alternate_units.forEach((u) => {
        list.push({
          value: `alt_${u.purchase_unit_id}`,
          label: `${u.unit_name} (${u.conversion_factor} ${item.unit} per ${u.unit_name})`,
          unit_name: u.unit_name,
          purchase_unit_id: u.purchase_unit_id,
          conversion_factor: parseFloat(u.conversion_factor) || 1.0,
        });
      });
    }

    return list;
  }, [selectedItem]);

  // Auto-select base unit when item is chosen
  const handleItemSelect = (opt) => {
    setSelectedItem(opt);
    if (opt && opt.item) {
      setSelectedUnit({
        value: `base_${opt.item.unit}`,
        label: `${opt.item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: opt.item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      });
    } else {
      setSelectedUnit(null);
    }
  };

  // Calculations
  const parsedQty = parseFloat(quantity) || 0;
  const factor = selectedUnit ? selectedUnit.conversion_factor : 1.0;
  const returnBaseQty = parsedQty * factor;
  const totalCustodyRem = selectedItem?.custodyRecord
    ? parseFloat(selectedItem.custodyRecord.remaining_base_quantity) || 0
    : 0;
  const isOverReturn = returnBaseQty > totalCustodyRem;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      toast.error("Please select a custodian.");
      return;
    }
    if (!selectedItem) {
      toast.error("Please select an item to return.");
      return;
    }
    if (!selectedUnit) {
      toast.error("Please select a return unit.");
      return;
    }
    if (parsedQty <= 0) {
      toast.error("Return quantity must be greater than zero.");
      return;
    }
    if (isOverReturn) {
      toast.error(
        `Cannot return ${returnBaseQty} base units. Custody balance is only ${totalCustodyRem}!`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        issue_id: selectedItem.custodyRecord?.id || null,
        user_id: selectedUser.value,
        inventory_item_id: selectedItem.value,
        quantity: parsedQty,
        purchase_unit_id: selectedUnit.purchase_unit_id,
        unit_name: selectedUnit.unit_name,
        verified_by: verifiedBy,
        notes: notes,
      };

      const res = await returnItems(payload);
      if (res.data?.success) {
        toast.success(
          `Stock return recorded successfully! (Ref: ${res.data.return_reference})`
        );
        setQuantity("");
        setNotes("");
        setVerifiedBy("");
        if (onReturnSuccess) onReturnSuccess();
      } else {
        toast.error(res.data?.message || "Failed to record return.");
      }
    } catch (err) {
      console.error("Return error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to record return.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-base-100 p-6 rounded-2xl border border-base-200 shadow-sm max-w-4xl mx-auto">
      <div className="border-b border-base-200 pb-4 mb-6">
        <h2 className="text-xl font-bold text-base-content flex items-center gap-2">
          <IconArrowDownLeft size={22} stroke={iconStroke} className="text-success" />
          Return Items to Store
        </h2>
        <p className="text-xs text-base-content/60 mt-1">
          Receive unsold items back from staff custody. Custody balance is closed out without inflating main warehouse stock.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Custodian & Product */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Returning Custodian / Staff <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={activeCustodians}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                setSelectedItem(null);
                setSelectedUnit(null);
              }}
              placeholder="Select staff with active custody..."
              isClearable
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Product to Return <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={heldItemOptions}
              value={selectedItem}
              onChange={handleItemSelect}
              isDisabled={!selectedUser}
              placeholder={selectedUser ? "Select product in custody..." : "Choose staff first"}
            />
          </div>
        </div>

        {/* Quantity & Unit */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Return Unit <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={unitOptions}
              value={selectedUnit}
              onChange={setSelectedUnit}
              isDisabled={!selectedItem}
              placeholder={selectedItem ? "Choose unit..." : "Select product first"}
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Return Quantity <span className="text-error">*</span>
            </label>
            <input
              type="number"
              step="any"
              min="0.001"
              required
              className={`input input-sm input-bordered w-full text-sm ${
                isOverReturn ? "input-error" : ""
              }`}
              placeholder="e.g. 5"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
        </div>

        {/* Conversion & Verification Preview Card */}
        {selectedItem && selectedUnit && parsedQty > 0 && (
          <div
            className={`p-4 rounded-xl border ${
              isOverReturn
                ? "bg-error/5 border-error/30 text-error"
                : "bg-success/5 border-success/20 text-base-content"
            }`}
          >
            <div className="flex items-center gap-2 mb-2 font-bold text-sm">
              <IconCalculator size={18} stroke={iconStroke} />
              Return Calculation & Custody Balance
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-base-content/60 block">Entered Qty</span>
                <span className="font-bold text-sm">
                  {parsedQty} {selectedUnit.unit_name}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Conversion</span>
                <span className="font-bold text-sm">
                  {factor} {selectedItem.item.unit} / {selectedUnit.unit_name}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Returned Base Qty</span>
                <span className="font-bold text-sm text-success">
                  {returnBaseQty.toLocaleString(undefined, { maximumFractionDigits: 3 })}{" "}
                  {selectedItem.item.unit}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Current Custody Balance</span>
                <span className="font-bold text-sm text-primary">
                  {totalCustodyRem.toLocaleString()} {selectedItem.item.unit}
                </span>
              </div>
            </div>

            {isOverReturn && (
              <div className="mt-3 flex items-center gap-1.5 text-xs text-error font-semibold">
                <IconAlertCircle size={16} stroke={iconStroke} />
                Return quantity exceeds outstanding custody by{" "}
                {(returnBaseQty - totalCustodyRem).toFixed(2)} {selectedItem.item.unit}!
              </div>
            )}
            {!isOverReturn && (
              <div className="mt-2 text-[11px] text-base-content/60 flex items-center gap-1">
                <IconInfoCircle size={14} stroke={iconStroke} />
                Remaining custody after return: {(totalCustodyRem - returnBaseQty).toFixed(2)}{" "}
                {selectedItem.item.unit}.
              </div>
            )}
          </div>
        )}

        {/* Supervisor & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Verified / Received By
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={verifiedBy}
              onChange={(e) => setVerifiedBy(e.target.value)}
              placeholder="e.g. Store Manager, Head Bartender"
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Remarks / Condition
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Sealed package, shift handover return"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-4 border-t border-base-200">
          <button
            type="submit"
            disabled={isSubmitting || isOverReturn || parsedQty <= 0 || !selectedItem}
            className="btn btn-success gap-2 px-8"
          >
            {isSubmitting ? (
              <IconRotate className="animate-spin" size={18} stroke={iconStroke} />
            ) : (
              <IconCheck size={18} stroke={iconStroke} />
            )}
            Confirm Return
          </button>
        </div>
      </form>
    </div>
  );
}
